import Layout from '@/components/feature/Layout';
import { useSeo } from '@/hooks/useSeo';
import { useFormSubmit } from '@/hooks/useFormSubmit';
import { categories, siteIntro } from '@/mocks/videos';

const CONTACT_URL = 'https://readdy.ai/api/form/db4niu6k6jkc2oct49ag';

const STEPS = [
  { icon: 'ri-compass-3-line', title: '发现内容', desc: '在首页浏览焦点推荐与热门视频，或按分类快速定位感兴趣的类型。' },
  { icon: 'ri-search-2-line', title: '搜索与筛选', desc: '使用顶部搜索框按标题或标签查找，分类页还可按类型、地区、年代筛选与排序。' },
  { icon: 'ri-play-circle-line', title: '进入详情', desc: '点击任意视频卡片进入详情页，查看简介、元信息与同类相关推荐。' },
  { icon: 'ri-share-forward-line', title: '分享好内容', desc: '在详情页一键复制链接，把喜欢的影片分享给身边的朋友。' },
];

export default function AboutPage() {
  const { status, error, submit } = useFormSubmit(CONTACT_URL);

  useSeo({
    title: '关于我们 - 啪啪视频',
    description:
      '了解啪啪视频的平台定位、内容范围与服务宗旨，并可通过反馈入口与我们取得联系，获取在线观看使用说明。',
    keywords: '啪啪视频,关于我们,视频平台介绍,联系方式,使用说明',
  });

  return (
    <Layout>
      <div className="pt-16">
        <section className="px-4 md:px-6 py-12 md:py-16 border-b border-background-200/70 bg-background-100/50">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-accent-500/15 text-accent-500 text-xs font-semibold">
              <i className="ri-information-line" />
              关于啪啪视频
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 leading-tight">
              {siteIntro.slogan}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{siteIntro.intro}</p>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">内容范围</h2>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            我们围绕「高清视频分享与在线播放」持续完善内容结构，覆盖多个主要类别，方便不同口味的观众快速找到喜欢的作品。
          </p>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categories.map((cat) => (
              <div key={cat.id} className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500/15 text-accent-500">
                  <i className={`${cat.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{cat.name}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{cat.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100/60 border-y border-background-200/70">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">使用说明</h2>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl">四步轻松找到并欣赏你想看的内容。</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {STEPS.map((step, i) => (
              <div key={step.title} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500/15 text-accent-500">
                    <i className={`${step.icon} text-2xl`} />
                  </span>
                  <span className="font-heading text-2xl font-extrabold text-background-300">0{i + 1}</span>
                </div>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">联系我们</h2>
              <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed">
                如果你对内容、功能有建议，或希望反馈问题，欢迎通过下方表单与我们联系。我们会认真阅读每一条反馈。
              </p>
              <ul className="mt-6 space-y-4">
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-accent-500">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">邮箱</p>
                    <p className="text-sm text-foreground-900">contact@papavideo.example</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-accent-500">
                    <i className="ri-time-line text-lg" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">服务宗旨</p>
                    <p className="text-sm text-foreground-900">内容优先，体验至上</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-6">
              <form id="pv-contact-form" data-readdy-form action={CONTACT_URL} onSubmit={submit} className="space-y-4">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="pv-form-note"
                />
                <div>
                  <label htmlFor="pv-name" className="block text-sm text-foreground-700 mb-1.5">称呼</label>
                  <input
                    id="pv-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full h-11 px-3 text-sm rounded-md bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="pv-email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="pv-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收我们的回复"
                    className="w-full h-11 px-3 text-sm rounded-md bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="pv-message" className="block text-sm text-foreground-700 mb-1.5">反馈内容</label>
                  <textarea
                    id="pv-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的建议或问题（最多 500 字）"
                    className="w-full px-3 py-2.5 text-sm rounded-md bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500 transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors disabled:opacity-60 w-full sm:w-auto whitespace-nowrap"
                >
                  <i className="ri-send-plane-line text-base" />
                  {status === 'loading' ? '提交中…' : '提交反馈'}
                </button>
                {status === 'success' && (
                  <p className="text-sm text-accent-500">提交成功，感谢你的反馈！</p>
                )}
                {status === 'error' && (
                  <p className="text-sm text-accent-400">{error || '提交失败，请稍后重试'}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}